import { Modal, useModals } from 'almanac-ui/Modal'
import { useDispatch } from 'react-redux'
import Styles from './EndReviewModal.module.scss'
import { InformalFeedbackRequest, InformalFeedbackRequestsClient } from 'api/clients/InformalFeedbackRequestsClient'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { ContentComposer } from 'components/ShareSheet/ContentComposer/ContentComposer'
import { useRef, useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useQueryClient } from 'react-query'
import { upsertInformalFeedbackRequest } from 'redux/actions/informalFeedbackRequests'
import { upsertReview } from 'redux/actions/reviews'
import { articleMetaSet } from 'redux/actions'
import { workspaceTasksKeys } from 'screens/Todos/hooks/useWorkspaceTasks'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { useAuth } from 'hooks/UseAuth'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { RecipientAvatars, UserAvatar } from 'common/ShareSheetV2/RecipientAvatars/RecipientAvatars'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useRefreshInformalFeedbackRequests } from 'hooks/useRefreshInformalFeedbackRequests'

interface Props {
  request: InformalFeedbackRequest
}

export const EndReviewModal = ({ request }: Props) => {
  const article = useCurrentArticleMeta()
  const contentRef = useRef(null)
  const queryClient = useQueryClient()
  const [endMessage, setEndMessage] = useState('')
  const { closeModal, closeAllModals } = useModals()
  const dispatch = useDispatch()
  const auth = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const { refreshRequests } = useRefreshInformalFeedbackRequests({
    almanacFileId: request.almanac_file.id,
  })

  const users = request.reviews.map((review) => {
    return {
      avatar_url: review.reviewer.avatar_url,
      display_name: review.reviewer.display_name,
    }
  }) as UserAvatar[]

  const handleSubmit = async () => {
    setIsLoading(true)
    const res = await InformalFeedbackRequestsClient.close(request.id, { message: endMessage })
    if (res.type === SUCCESS_RESPONSE_TYPE) {
      const request = res.data.informal_feedback_request
      showToastMessage('Review ended successfully!')
      refreshRequests()
      closeAllModals()

      dispatch(upsertInformalFeedbackRequest(request))
      dispatch(upsertReview(request))
      article && dispatch(articleMetaSet({ ...article, workflow_status: undefined }))
      queryClient.invalidateQueries(workspaceTasksKeys.navMenuCounter(request.company_key))
    } else {
      setIsLoading(false)
      showErrorMessageToast('An error occurred! Our team has been notified and we are looking into it.')
      reportError('Failed to cancel review request', {
        severity: 'error',
        metaData: { request, auth },
      })
    }
  }

  return (
    <Modal.Root>
      <div className={Styles.container}>
        <div className={Styles.body}>
          <RecipientAvatars users={users} size={40} />
          <Heading level={3}>You are about to end this review</Heading>
          <div className={Styles.label}>
            <Text variant="secondary" color="var(--dark-80)">
              Everyone will be notified that the review is over. Anyone who has access to the file will still keep it.
            </Text>
          </div>

          <div className={Styles.messageWrapper}>
            <ContentComposer
              ref={contentRef}
              content={endMessage}
              placeholder="Type your message here (optional)..."
              onChange={setEndMessage}
            />
          </div>
        </div>
      </div>
      <div className={Styles.footer}>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button size="lg" variant="tertiary" onPress={closeModal} isDisabled={isLoading}>
              Close
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Button size="lg" variant="primary" onPress={handleSubmit} isDisabled={isLoading}>
              End Review
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </div>
    </Modal.Root>
  )
}
